import { getNumberFromQueryString } from '@if/core/utils'
import { Banner, Caption, getErrorMessage, SplitContainer, useListNavigation, useShortcut } from '@if/mui'
import { Card, CardContent, Stack } from '@mui/material'
import {
  InteracWireSearchPossibleMatchFilters,
  useInteracWireByFilters,
  useSearchPossibleMatch,
} from '@src/api/interac-wire-api'
import { ESystemMatchingResult, InteracPossibleMatch, InteracWireFiltersType } from '@src/types/interac'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { useInteracCandidateNotesDialog } from './interac-candidate-notes-hook'
import { SELECTED_TRANSFER_PARAM, useInteracQueueCursor } from './interac-queue-cursor-hook'
import { useInteracTerminalActions } from './interac-terminal-actions-hook'
import InteracCandidatesCard from './InteracCandidatesCard'
import InteracQueueCard from './InteracQueueCard'
import { InteracQueueFilterValues } from './InteracQueueFilters'
import InteracTransferCard from './InteracTransferCard'

const QUEUE_LIMIT = 50
const EMPTY_CANDIDATES: InteracPossibleMatch[] = []
const PREVIOUS_TRANSFER_KEY = { key: 'ArrowUp' }
const NEXT_TRANSFER_KEY = { key: 'ArrowDown' }
const QUEUE_LIST_ID = 'interac-queue'

const InteracReconciliation = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchFilters, setSearchFilters] = useState<InteracWireSearchPossibleMatchFilters | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const rehomeCandidates = useRef<() => void>(() => {})

  const filters = useMemo<InteracWireFiltersType>(
    () => ({
      limit: getNumberFromQueryString(searchParams, 'limit', QUEUE_LIMIT),
      offset: 0,
      isUnmatchedWire: true,
      clientNameContains: searchParams.get('clientNameContains') || null,
      email: searchParams.get('email') || null,
      systemMatchingResult:
        (searchParams.get('systemMatchingResult') as ESystemMatchingResult | null) ?? ESystemMatchingResult.Sure,
    }),
    [searchParams],
  )

  const [queue, isQueueFetching, queueError] = useInteracWireByFilters(filters)
  const cursor = useInteracQueueCursor(queue, QUEUE_LIST_ID)
  const wire = cursor.transfer

  const [searchResults, isSearching] = useSearchPossibleMatch(wire?.id ?? null, searchFilters)
  const candidates = useMemo(
    () => (searchFilters ? searchResults : (wire?.possibleMatches ?? EMPTY_CANDIDATES)),
    [searchFilters, searchResults, wire?.possibleMatches],
  )

  const notesDialog = useInteracCandidateNotesDialog()
  const actions = useInteracTerminalActions(wire)

  const runDialog = useCallback(async <T,>(open: () => Promise<T>) => {
    setDialogOpen(true)
    try {
      return await open()
    } finally {
      setDialogOpen(false)
    }
  }, [])

  const runTerminalAction = useCallback(
    async (action: () => Promise<boolean>) => {
      if (await runDialog(action)) {
        cursor.advancePast(cursor.index)
        rehomeCandidates.current()
      }
    },
    [cursor, runDialog],
  )

  const match = useCallback(
    (candidate: InteracPossibleMatch | undefined) => {
      if (!wire || !candidate) return
      void runTerminalAction(() => actions.confirmMatch(wire, candidate))
    },
    [actions, runTerminalAction, wire],
  )

  const candidatesNav = useListNavigation({
    count: candidates.length,
    orientation: 'horizontal',
    focus: 'ambient',
    enabled: !dialogOpen,
    onActivate: (index) => match(candidates[index]),
  })

  // Arrowing onto a candidate *is* choosing it — ← → are the only way to pick one
  // from the keyboard, and Enter then confirms the highlighted row. So the pick is
  // read off the nav cursor rather than kept as a second piece of state that the
  // arrows would leave behind.
  const selectedLoanId = candidates[candidatesNav.active]?.loanId ?? null

  // The cursor is the pick, so it must never point into the previous transfer's
  // list: every transfer change re-homes it and drops the candidate search with it.
  const onTransferChanged = useCallback(() => {
    candidatesNav.setActive(0)
    setSearchFilters(null)
  }, [candidatesNav])
  rehomeCandidates.current = onTransferChanged

  const moveQueue = useCallback(
    (delta: number) => {
      if (!cursor.move(delta)) return
      onTransferChanged()
    },
    [cursor, onTransferChanged],
  )

  const selectTransfer = useCallback(
    (index: number) => {
      if (index === cursor.index) return
      cursor.moveTo(index)
      onTransferChanged()
    },
    [cursor, onTransferChanged],
  )

  useShortcut(PREVIOUS_TRANSFER_KEY, () => moveQueue(-1), { enabled: !dialogOpen })
  useShortcut(NEXT_TRANSFER_KEY, () => moveQueue(1), { enabled: !dialogOpen })

  const onFiltersChange = useCallback(
    (values: InteracQueueFilterValues) => {
      const search = values.search?.trim() || null
      const isEmail = search?.includes('@') ?? false
      setSearchFilters(null)
      candidatesNav.setActive(0)
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous)
          next.set('systemMatchingResult', values.systemMatchingResult)
          if (search && isEmail) next.set('email', search)
          else next.delete('email')
          if (search && !isEmail) next.set('clientNameContains', search)
          else next.delete('clientNameContains')
          next.delete(SELECTED_TRANSFER_PARAM)
          return next
        },
        { replace: true },
      )
    },
    [candidatesNav, setSearchParams],
  )

  const onSearchChange = useCallback(
    (next: InteracWireSearchPossibleMatchFilters | null) => {
      setSearchFilters(next)
      candidatesNav.setActive(0)
    },
    [candidatesNav],
  )

  const aside = (
    <InteracQueueCard
      queue={queue}
      cursor={cursor}
      onSelect={selectTransfer}
      filters={filters}
      isFiltered={Boolean(filters.clientNameContains || filters.email)}
      onFiltersChange={onFiltersChange}
    />
  )

  const main = wire ? (
    <Stack spacing={3}>
      <InteracTransferCard wire={wire} />
      <InteracCandidatesCard
        wire={wire}
        candidates={candidates}
        nav={candidatesNav}
        isSearching={isSearching}
        searchFilters={searchFilters}
        onSearchChange={onSearchChange}
        selectedLoanId={selectedLoanId}
        keysEnabled={!dialogOpen}
        onPick={candidatesNav.setActive}
        onMatch={() => match(candidates[candidatesNav.active])}
        onNoMatch={() => void runTerminalAction(() => actions.confirmNoMatch(wire))}
        onDiscard={() => void runTerminalAction(() => actions.confirmDiscard(wire))}
        onOpenNotes={(candidate) => void runDialog(() => notesDialog.open(candidate))}
      />
    </Stack>
  ) : (
    !isQueueFetching && (
      <Card>
        <CardContent>
          <Caption>{t('interacWire.transfer.none')}</Caption>
        </CardContent>
      </Card>
    )
  )

  return (
    <Stack spacing={3}>
      {queueError && <Banner severity="error">{getErrorMessage(queueError, t('common.messages.error'))}</Banner>}
      <SplitContainer asidePosition="left" asideSize="standard" asideStack="above" aside={aside} main={main} />
    </Stack>
  )
}

export default InteracReconciliation
